import { useTranslation } from "react-i18next";
import { formatNumber } from "../../lib/format";
import { Promena } from "./Promena";

/**
 * Pločica izveštaja: izmerena veličina, promena prema prošloj godini, i prošlogodišnja vrednost.
 *
 * Crta se klasama dizajn sistema (`kpi`, `label`, `val`, `tag`), ne sopstvenim stilovima — isti
 * oblik nose pločice kvaliteta, laboratorije i ugovora, pa ekran izveštaja ne sme da izgleda kao
 * tuđ posao.
 *
 * **Promena se piše kao procenat, ne kao „indeks".** Štampani izveštaj u tabelama piše „Indeks" i
 * vrednost 1,31 — tamo ta reč i ostaje, jer se naše kolone redom porede sa njegovim. Ovde ne:
 * pločica je naša i čita je čovek kome „+31 %" kaže sve odmah, dok „indeks 1.31" traži da se u
 * glavi oduzme jedinica i pomnoži sa sto.
 *
 * **Broj ide sa tačno dve decimale.** Podrazumevani `Intl` daje do tri, pa je zbir koji je u bazi
 * 6.837.535,40 na ekranu ispadao „6.837.535,399" — brojka koja izgleda kao greška u merenju a nije.
 */

const DVE = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

export interface KpiTileProps {
  label: string;
  value: number;
  /** kg, RSD… Stoji uz broj, sitnije, da se broj čita prvi. */
  unit?: string;
  /** Ista veličina godinu ranije; bez nje se promena ne crta. */
  previous?: number | null;
  locale: string;
  /**
   * Da li je veći broj bolji. Za količinu, vrednost i RUC jeste; za dospelo potraživanje nije,
   * pa rast tamo ne sme da bude zelen.
   */
  moreIsBetter?: boolean;
  /** Sitan natpis ispod, za ono što brojka sama ne kaže (npr. čija je raspodela). */
  note?: string;
  /** `false` kad odnos ne znači ništa — nepotpun period. Prošlogodišnja vrednost se i dalje piše. */
  tumaci?: boolean;
}

export function KpiTile({
  label,
  value,
  unit,
  previous,
  locale,
  moreIsBetter = true,
  note,
  tumaci,
}: KpiTileProps) {
  const { t } = useTranslation();

  return (
    <div className="kpi">
      <div className="label">{label}</div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span className="val" style={value < 0 ? { color: "var(--err)" } : undefined}>
          {formatNumber(value, locale, DVE)}
        </span>
        {unit && <span style={{ fontSize: 13, color: "var(--ink-3)", fontWeight: 500 }}>{unit}</span>}
        {/* Isti čip koji nosi i ćelija tabele: ista veličina ne sme da ima dva zapisa. */}
        <Promena sada={value} pre={previous} viseJeBolje={moreIsBetter} locale={locale} tumaci={tumaci} />
      </div>

      <div className="sub" style={{ fontSize: 12, color: "var(--ink-3)" }}>
        {previous == null || !Number.isFinite(previous)
          ? t("report.kpi.noComparison", "prošle godine nema podatka")
          : t("report.kpi.lastYear", {
              iznos: formatNumber(previous, locale, DVE),
              defaultValue: "prošle godine {{iznos}}",
            })}
        {note && <> · {note}</>}
      </div>
    </div>
  );
}
